/* eslint-disable no-debugger */
import { Page } from '@4beefopag/4bee-fopag-core/dist/organisms';
import { useHttpRequest } from '@4beefopag/4bee-fopag-core/dist/services/hooks';
import { message, Modal } from 'antd';
import fileDownload from 'js-file-download';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useUserPolicy } from '../../../user-policy';
import * as BenefitOrderRepository from '../../../services/repository/benefitOrderRepository';
import mostrarErro from '../../../services/util/error-handler';
import { ContainerTela } from '../../atoms/Apontamento';
import BenefitOrderButtons from '../../molecules/BenefitOrderButtons';
import ErrorDisplay from '../../molecules/ErrorDisplay';
import BenefitOrderTable from '../../organisms/BenefitOrderTable';
import OrderBenefitSummary from '../../organisms/OrderBenefitSummary';
import ModalApplyValuesToAll from './ModalApplyValuesToAll';
import ModalRequestReopening from './ModalRequestReopening';

const WrapperError = styled.div`
  margin-top: 50px;
`;

function PedidoBeneficio({ viewMode }) {
  const navigate = useNavigate();

  const [selectedFolha, setSelectedFolha] = useState({});
  const [error, setError] = useState(null);
  const [benefitOrderEdit, setBenefitOrderEdit] = useState(undefined);
  const [isApplyValueToAllModalOpen, setIsApplyValueToAllModalOpen] = useState(false);
  const [isRequestReopeningModalOpen, setIsRequestReopeningModalOpen] = useState(false);
  const { id: benefitOrderIdParam } = useParams();
  const { userAccount } = useUserPolicy();

  const {
    state: { data: benefitOrder, loading: loadingBenefitOrder, error: errorToGetBenefit },
  } = useHttpRequest(
    async () => {
      setError(null);
      if (viewMode && benefitOrderIdParam && !selectedFolha.id) {
        const benefitOrderFound = await BenefitOrderRepository.findById(benefitOrderIdParam);
        setSelectedFolha({ id: benefitOrderFound.companyId });
        return benefitOrderFound;
      }
      return selectedFolha && selectedFolha.id && BenefitOrderRepository.findOrCreateBenefitOrder(selectedFolha.id);
    },
    {},
    [selectedFolha]
  );

  useEffect(() => {
    setBenefitOrderEdit(benefitOrder && benefitOrder.id ? { ...benefitOrder } : undefined);
  }, [benefitOrder]);

  useEffect(() => {
    if (errorToGetBenefit && viewMode && errorToGetBenefit?.response?.status === 404) {
      navigate('/not-found');
    }
    if (
      errorToGetBenefit?.response?.data &&
      errorToGetBenefit?.response?.status >= 400 &&
      errorToGetBenefit?.response?.status < 500
    ) {
      setError(errorToGetBenefit?.response?.data);
    }
  }, [errorToGetBenefit]);

  const calcTotal = (benefitOrderCooperatorList) =>
    benefitOrderCooperatorList.reduce(
      (acc, cooperatorOrder) => ({
        totalFoodVoucher: acc.totalFoodVoucher + cooperatorOrder.foodVoucherValue,
        totalMealVoucher: acc.totalMealVoucher + cooperatorOrder.mealVoucherValue,
      }),
      { totalFoodVoucher: 0, totalMealVoucher: 0 }
    );

  const onChangeCooperatorValue = (cooperatorId, key, value) => {
    const newBenefitOrderCooperators = benefitOrderEdit.benefitOrderCooperators.map((cooperatorOrder) => {
      if (cooperatorOrder.cooperator.id === cooperatorId) {
        return {
          ...cooperatorOrder,
          [key]: value,
        };
      }
      return cooperatorOrder;
    });
    const { totalFoodVoucher, totalMealVoucher } = calcTotal(newBenefitOrderCooperators);
    setBenefitOrderEdit({
      ...benefitOrderEdit,
      totalFoodVoucher,
      totalMealVoucher,
      benefitOrderCooperators: newBenefitOrderCooperators,
    });
  };

  const onSave = async () => {
    try {
      const benefitOrderUpdated = await BenefitOrderRepository.saveBenefitOrder(benefitOrderEdit.id, benefitOrderEdit);
      setBenefitOrderEdit(benefitOrderUpdated);
      Modal.success({
        title: 'Sucesso',
        content: <p>Pedido salvo com sucesso!</p>,
      });
    } catch (err) {
      mostrarErro(err);
    }
  };

  const onApplyValuesToAll = ({ foodValue, mealValue, shouldApplyToMeal, shouldApplyToFood }) => {
    const newBenefitOrderCooperators = benefitOrderEdit.benefitOrderCooperators.map((cooperatorOrder) => ({
      ...cooperatorOrder,
      mealVoucherValue: shouldApplyToMeal ? mealValue : cooperatorOrder.mealVoucherValue,
      foodVoucherValue: shouldApplyToFood ? foodValue : cooperatorOrder.foodVoucherValue,
    }));

    const { totalFoodVoucher, totalMealVoucher } = calcTotal(newBenefitOrderCooperators);
    setBenefitOrderEdit({
      ...benefitOrderEdit,
      totalFoodVoucher,
      totalMealVoucher,
      benefitOrderCooperators: newBenefitOrderCooperators,
    });
  };

  const onConfirmRequestReopening = async (justification) => {
    try {
      const benefitOrderUpdated = await BenefitOrderRepository.requestReopening(benefitOrderEdit.id, justification);
      setBenefitOrderEdit(benefitOrderUpdated);
      setIsRequestReopeningModalOpen(false);
      Modal.success({
        title: 'Sucesso',
        content: <p>Solicitação de alteração do período concluída com sucesso!</p>,
      });
    } catch (err) {
      mostrarErro(err);
    }
  };

  const onDownloadDetailReport = async () => {
    const hide = message.loading('Seu download está sendo preparado');
    try {
      const fileStreamDownload = await BenefitOrderRepository.downloadDetailXlsxReport(benefitOrder.id);
      const fileName = `Pedido de Vale ${benefitOrder.company.id} ${
        benefitOrder.company.name
      } ${benefitOrder.competence.format('MM_YYYY')}.xlsx`;
      fileDownload(fileStreamDownload, fileName);
    } catch (err) {
      mostrarErro(err);
    } finally {
      hide();
    }
  };

  const checkEmptyValue = async () =>
    new Promise((res, rej) => {
      if (
        benefitOrderEdit.benefitOrderCooperators.some(
          (cooperator) => cooperator.foodVoucherValue <= 0 || cooperator.mealVoucherValue <= 0
        )
      ) {
        Modal.confirm({
          title: 'Finalizar Pedido de Vale',
          content: (
            <div>
              <p>
                Durante a finalização do pedido, foi verificado que existem cooperados sem valor de VA e/ou VR
                informados.
              </p>
              <p>
                Caso deseje, pode continuar com a solicição do pedido, mas os cooperados com valor zerado não receberão
                o benefício.
              </p>
            </div>
          ),
          okText: 'Prosseguir',
          cancelText: 'Fechar',
          onOk: () => res(),
          onCancel: () => rej(),
        });
      } else {
        res();
      }
    });

  const sendBenefitOrder = async () => {
    try {
      const benefitOrderUpdated = await BenefitOrderRepository.sendBenefitOrder(benefitOrderEdit.id, benefitOrderEdit);
      setBenefitOrderEdit(benefitOrderUpdated);
      Modal.success({
        title: 'Sucesso',
        content: <p>Pedido enviado com sucesso!</p>,
      });
    } catch (err) {
      mostrarErro(err);
    }
  };

  const onSend = async () => {
    try {
      await checkEmptyValue();
      // So deve prosseguir apos o ok
      await sendBenefitOrder();
    } catch (err) {
      /* empty */
    }
  };

  const renderOrder = () => {
    if (error) {
      return (
        <WrapperError>
          <ErrorDisplay
            image="https://storage.googleapis.com/static-files-4fdd9378-835d-40e4-b124-36699ae5cc08/coop/buying-items.svg"
            title={`Opsss! Desculpe ${userAccount?.name || ''}...`}
            description={error?.message}
          />
        </WrapperError>
      );
    }
    if (benefitOrderEdit) {
      return (
        <BenefitOrderTable
          isEditable={benefitOrderEdit.isEditable}
          cooperators={benefitOrderEdit.benefitOrderCooperators}
          onChangeCooperatorValue={onChangeCooperatorValue}
          buttons={
            <BenefitOrderButtons
              onApplyValuesToAll={() => setIsApplyValueToAllModalOpen(true)}
              onRequestReopening={() => setIsRequestReopeningModalOpen(true)}
              benefitOrder={benefitOrderEdit}
              onSave={onSave}
              onDownloadDetailReport={onDownloadDetailReport}
              onSend={onSend}
            />
          }
        />
      );
    }
    return null;
  };

  return (
    <Page
      projeto="Benefícios"
      titulo="Pedido de Vale"
      linkManual={process.env.REACT_APP_LINK_MANUAL}
    >
      <OrderBenefitSummary
        loading={loadingBenefitOrder}
        benefitOrder={benefitOrderEdit}
        companyId={selectedFolha ? selectedFolha.id : 0}
        disableCompanySelect={!!viewMode}
        onChangeFolha={setSelectedFolha}
        hideInfoCards={!!error}
      />
      <ContainerTela>{renderOrder()}</ContainerTela>

      {/** ************************* modais ************************** */}
      <ModalApplyValuesToAll
        open={isApplyValueToAllModalOpen}
        onClose={() => setIsApplyValueToAllModalOpen(false)}
        onApply={onApplyValuesToAll}
      />
      <ModalRequestReopening
        open={isRequestReopeningModalOpen}
        onCancelClick={() => setIsRequestReopeningModalOpen(false)}
        onConfirmClick={onConfirmRequestReopening}
      />
    </Page>
  );
}

PedidoBeneficio.propTypes = {
  viewMode: PropTypes.bool,
};

PedidoBeneficio.defaultProps = {
  viewMode: false,
};

export default PedidoBeneficio;
